import React from "react";
import { useState, useMemo, useContext } from "react";
import { Button, Col, Divider, Form, Input, Flex, Row, Select, InputNumber, App, Typography, Tooltip, Empty, Grid, Modal, ModalFuncProps, Popconfirm, Space, Tour } from "antd";

import { QuestionCircleTwoTone } from "@ant-design/icons";

import { DeleteFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { PlanoContasService } from "../../../Services/Financeiro/PlanoContasService";
import { PlanoContasTipoService } from "../../../Services/Financeiro/PlanoContasTipoService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { EmpresaService } from '../../../Services/Pessoa/EmpresaService';
import { SelectComPesquisa } from "../../../Components/SelectComPesquisa";
import { InputComReactIMask } from "../../../Components/InputComReactIMask";
import { TypePlanoContas } from "../../../Models/Financeiro/PlanoContas";
import { TypeProporcao } from "../../../Models/Financeiro/Proporcao";
import { CentroCustoService } from "../../../Services/Financeiro/CentroCustoService";
import { ITutorial } from "../../../Components/AdicionarAjuda";
import { PropriedadesDeNotificacao } from "../../../Utils/Notificacoes";
import { useForm, useWatch } from "antd/es/form/Form";
import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";

import { Formatacoes } from "../../../Utils/Formatacao";
import { SessaoContext } from "../../../Contexts/SessaoContext";
import { Permissoes } from "../../../Constants/Permissoes";

import AdicionarAjuda from "../../../Components/AdicionarAjuda";
import * as Tutoriais from "./cadastroDePlanoDeContas.tutorial"
import { EnumRegistroTipo } from "../../../Models/Financeiro/PlanoContasTipoRegistro";

enum TipoDeRegistro {
    Grupo = 1,
    Subgrupo = 2,
    Item = 3
}

export const CadastroDePlanoDeConta: React.FC<{
    aoCancelar?: () => any,
    depoisDeFinalizar?: () => any,
    planoDeContasEmAlteracao?: TypePlanoContas
}> = (props) => {

    const usuario = useContext(SessaoContext)?.sessao?.usuario;

    const planoContasService = new PlanoContasService();
    const centroCustoService = new CentroCustoService();
    const planoContasTipoService = new PlanoContasTipoService();
    const empresaService = new EmpresaService();

    const { notification } = App.useApp();

    const { confirm } = Modal;
    const pedirConfirmacao = (props: ModalFuncProps) => {
        confirm(props);
    };

    const [ formularioDePlanoDeContas ] = useForm<TypePlanoContas>();
    const valoresDoFormularioDePlanoDeContas = useWatch([], formularioDePlanoDeContas);
    const classificacaoSelecionada = valoresDoFormularioDePlanoDeContas?.tipoRegistro?.codigo;

    const [ formularioDeProporcoes ] = useForm<TypeProporcao>();
    const [listaProporcoes, setListaProporcoes] = useState<TypeProporcao[]>(
        ( props.planoDeContasEmAlteracao?.tipoRegistro?.codigo === TipoDeRegistro.Item ) && props.planoDeContasEmAlteracao.proporcoes?
        props.planoDeContasEmAlteracao.proporcoes : []
    );
    const somaDasProporcoes = useMemo(() => listaProporcoes.reduce((prev, cur) => prev + (cur.proporcao ?? 0), 0), [listaProporcoes]);

    const [mostrarTutorialDoCadastroDeProporcoes, setMostrarTutorialDoCadastroDeProporcoes] = useState(false);
    const estaEditando = useMemo(() => {
        return props.planoDeContasEmAlteracao != undefined;
    }, [props.planoDeContasEmAlteracao])

    const tutoriais: ITutorial[] = [
        {
            titulo: estaEditando? "Como editar um grupo" : "Como adicionar um novo grupo",
            tutorial: {
                steps: classificacaoSelecionada == TipoDeRegistro.Grupo? 
                    Tutoriais.AdicionarGrupo :
                    [{
                        title: "Seleção",
                        description: `Antes de tudo, selecione "Grupo" na classificação.`,
                        target: () => document.getElementById(Tutoriais.IDs.campoClassificacao)!,
                    }]
            }
        },
        {
            titulo: estaEditando? "Como editar um subgrupo" : "Como adicionar um novo subgrupo",
            tutorial: {
                steps: classificacaoSelecionada == TipoDeRegistro.Subgrupo? 
                    Tutoriais.AdicionarSubGrupo :
                    [{
                        title: "Seleção",
                        description: `Antes de tudo, selecione "Subgrupo" na classificação.`,
                        target: () => document.getElementById(Tutoriais.IDs.campoClassificacao)!,
                    }]
            }
        },
        {
            titulo: estaEditando? "Como editar um item" : "Como adicionar um novo item",
            tutorial: {
                steps: classificacaoSelecionada == TipoDeRegistro.Item? 
                    Tutoriais.AdicionarItem :
                    [{
                        title: "Seleção",
                        description: `Antes de tudo, selecione "Item" na classificação.`,
                        target: () => document.getElementById(Tutoriais.IDs.campoClassificacao)!,
                    }]
            }
        }
    ]


    const listaEmpresas = useQuery({
        queryKey: ['listarEmpresas'],
        queryFn: () => empresaService.listar({ ativo: true }),
        enabled: true,
        refetchOnWindowFocus: false
    });

    const listarTipo = useQuery({
        queryKey: ['listarTipo'],
        queryFn: () => planoContasTipoService.listarTipo({}),
        refetchOnWindowFocus: false
    });

    const listarTipoClassificacao = useQuery({
        queryKey: ['listarTipoClassificacao'],
        queryFn: () => planoContasTipoService.listarClassificacao({}),
        refetchOnWindowFocus: false
    });

    const listarGrupos = useQuery({
        queryKey: ['listarGrupos'],
        queryFn: () => planoContasService.listar({ tipoRegistro: { codigo: 1 } }),
        refetchOnWindowFocus: false
    });

    const listarSubGrupos = useQuery({
        queryKey: ['listarSubGrupos'],
        queryFn: () => planoContasService.listar({ tipoRegistro: { codigo: 2 } }),
        refetchOnWindowFocus: false
    });

    const listarCentroDeCusto = useQuery({
        queryKey: ["listarCentroDeCusto"],
        queryFn: () => centroCustoService.listar({ tipo: { codigo: 2 } }),
        enabled: true,
        refetchOnWindowFocus: false
    })

    const handleRemoverProporcao = (codProporcao: any) => {
        setListaProporcoes(l => l.filter(item => item.codigo !== codProporcao))
    }

    const handleIncluirCentroCusto = (valores: TypeProporcao) => {
        if(valores.proporcao == undefined || valores.centroCusto == undefined) return;

        if (somaDasProporcoes + valores.proporcao <= 100) {
            const novaProporcao: TypeProporcao = {
                codigo: valores.centroCusto.codigo,
                centroCusto: {
                    codigo: valores.centroCusto.codigo,
                    descricao: listarCentroDeCusto.data?.data.find(e => e.codigo === valores.centroCusto!.codigo)?.descricao
                },
                proporcao: valores.proporcao
            }

            setListaProporcoes(oldArray => [...oldArray, novaProporcao])

            formularioDeProporcoes.resetFields();
        } else {
            notification.error({ message: "A soma das proporções não pode exceder 100.", placement: "topRight" });
        }
    }

    const handleSalvarWrapper = (values: TypePlanoContas) => {
        // if (classificacaoSelecionada === TipoDeRegistro.Item && listaProporcoes.length === 0) {
        //     notification.error({ message: "Atenção: Adicione um centro de custo!", placement: "topRight" });
        // } else 
        // if (classificacaoSelecionada === TipoDeRegistro.Item && somaDasProporcoes < 100) {
        //     notification.error({ message: "Atenção: É necessário que a proporção total esteja em 100%.", placement: "topRight" });
        // } else {
        if (!props.planoDeContasEmAlteracao) {
            handleInserir.mutate(values);
        } else {
            handleAtualizar.mutate(values);
        }
        // }
    };

    const handleInserir = useMutation({
        mutationFn: (values: TypePlanoContas) => {
            const novoPlanoContas: TypePlanoContas = {
                codigoContabil: values.codigoContabil,
                empresa: { codigo: values.empresa?.codigo },
                tipoRegistro: { codigo: values.tipoRegistro?.codigo },
                tipo: { codigo: values.tipo?.codigo },
                descricao: values.descricao,
                proporcoes: listaProporcoes
            }

            switch (classificacaoSelecionada) {
                case 2: {
                    novoPlanoContas.grupo = { codigo: values.grupo?.codigo };
                    break;
                }
                case 3: {
                    novoPlanoContas.grupo = { codigo: values.grupo?.codigo };
                    novoPlanoContas.subGrupo = { codigo: values.subGrupo?.codigo };
                    break;
                }
            }

            return planoContasService.inserir(novoPlanoContas);
        },
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro realizado com sucesso.')
            });

            props.depoisDeFinalizar?.();
        },
    });

    const handleAtualizar = useMutation({
        mutationFn: (values: TypePlanoContas) => {
            const novoPlanoContas: TypePlanoContas = {
                codigo: props.planoDeContasEmAlteracao?.codigo,
                codigoContabil: values.codigoContabil,
                empresa: { codigo: values.empresa?.codigo },
                tipoRegistro: { codigo: values.tipoRegistro?.codigo },
                tipo: { codigo: values.tipo?.codigo },
                descricao: values.descricao,
                proporcoes: listaProporcoes
            }

            switch (classificacaoSelecionada) {
                case 2: {
                    novoPlanoContas.grupo = { codigo: values.grupo?.codigo };
                    break;
                }
                case 3: {
                    novoPlanoContas.grupo = { codigo: values.grupo?.codigo };
                    novoPlanoContas.subGrupo = { codigo: values.subGrupo?.codigo };
                    break;
                }
            }

            return planoContasService.atualizar(novoPlanoContas);
        },
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro atualizado com sucesso.')
            });

            props.depoisDeFinalizar?.();
        },
    });

    return (
        <>
            <Tour
                open={mostrarTutorialDoCadastroDeProporcoes}
                onClose={() => setMostrarTutorialDoCadastroDeProporcoes(false)}
                steps={Tutoriais.AdicionarProporcoes}
            />

            <Form<TypePlanoContas> form={formularioDePlanoDeContas} name="adicionar" layout="vertical" onFinish={handleSalvarWrapper} autoComplete="off">
                <Row gutter={16}>
                    <Col xs={24} md={8} id={Tutoriais.IDs.campoEmpresa}>
                        <Form.Item<TypePlanoContas>
                            name={["empresa", "codigo"]}
                            rules={[{ required: true, message: "Por favor, informe uma empresa!" }]}
                            initialValue={props.planoDeContasEmAlteracao?.empresa?.codigo}
                            label={"Empresa"}
                        >
                            <SelectComPesquisa
                                placeholder={"Selecione"}
                                allowClear
                                onChange={() => {
                                    const valorAnterior = valoresDoFormularioDePlanoDeContas.empresa?.codigo;

                                    if (valorAnterior == undefined) return;

                                    pedirConfirmacao({
                                        title: "Você tem certeza?",
                                        icon: <ExclamationCircleFilled />,
                                        content: "Todas as proporções até aqui adicionadas serão perdidas. O grupo e subgrupo selecionados serão anulados.",
                                        okText: "Continuar",
                                        onOk() {
                                            formularioDePlanoDeContas.setFieldValue(["grupo", "codigo"], undefined);
                                            formularioDePlanoDeContas.setFieldValue(["subGrupo", "codigo"], undefined);
                                            setListaProporcoes([]);
                                        },
                                        onCancel() {
                                            formularioDePlanoDeContas.setFieldValue(["empresa", "codigo"], valorAnterior);
                                        },
                                    });
                                }}
                                options={listaEmpresas.data?.data.map((x) => {
                                    return {
                                        value: x.codigo,
                                        label: x.nome!,
                                    };
                                })}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8} id={Tutoriais.IDs.campoTipo}>
                        <Form.Item<TypePlanoContas>
                            name={["tipo", "codigo"]}
                            rules={[{ required: true, message: "Por favor, informe um tipo!" }]}
                            initialValue={props.planoDeContasEmAlteracao?.tipo?.codigo}
                            label={"Tipo"}
                        >
                            <Select
                                placeholder={"Selecione"}
                                allowClear
                                options={listarTipo.data?.data.map((x) => {
                                    return {
                                        value: x.codigo,
                                        label: x.tipo,
                                    };
                                })}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8} id={Tutoriais.IDs.campoClassificacao}>
                        <Form.Item<TypePlanoContas>
                            name={["tipoRegistro", "codigo"]}
                            label={"Classificação"}
                            initialValue={props.planoDeContasEmAlteracao?.tipoRegistro?.codigo}
                            rules={[{ required: true, message: "Por favor, informe uma classificação!" }]}
                        >
                            <SelectComPesquisa
                                placeholder={"Selecione"}
                                allowClear
                                onChange={(val) => {
                                    const valorAnterior = valoresDoFormularioDePlanoDeContas.tipoRegistro?.codigo;
                                    if (valorAnterior == undefined || listaProporcoes.length == 0 || valorAnterior != TipoDeRegistro.Item) return;

                                    pedirConfirmacao({
                                        title: "Você tem certeza?",
                                        icon: <ExclamationCircleFilled />,
                                        content:
                                            "Sua mudança coloca em risco as proporções anteriormente cadastradas, as quais serão perdidas se as alterações forem persistidas.",
                                        okText: "Continuar",
                                        onOk() {},
                                        onCancel() {
                                            formularioDePlanoDeContas.setFieldValue(["tipoRegistro", "codigo"], valorAnterior);
                                        },
                                    });
                                }}
                                options={listarTipoClassificacao.data?.data.map((x) => {
                                    return {
                                        value: x.codigo,
                                        label: x.tipo,
                                    };
                                })}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={7} id={Tutoriais.IDs.campoDescricao}>
                        <Form.Item<TypePlanoContas>
                            name={["descricao"]}
                            rules={[{ required: true, message: "Por favor, informe uma descrição!" }]}
                            initialValue={props.planoDeContasEmAlteracao?.descricao}
                            label={"Descrição"}
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6} lg={4} id={Tutoriais.IDs.campoCodigoContabil}>
                        <Form.Item<TypePlanoContas> shouldUpdate={(anterior, atual) => anterior?.tipoRegistro?.codigo != atual?.tipoRegistro?.codigo}>
                            {
                                ({getFieldValue}) => {
                                    const tipoRegistro = getFieldValue([ "tipoRegistro", "codigo" ]);
                                    return <Form.Item<TypePlanoContas>
                                        name={["codigoContabil"]}
                                        rules={[{ len : tipoRegistro }]}
                                        initialValue={props.planoDeContasEmAlteracao?.codigoContabil}
                                        normalize={Formatacoes.somenteDigitos}
                                        label={"Código contábil"}
                                    >
                                        <Input placeholder="Código contábil" />
                                    </Form.Item>;
                                }
                            }
                        </Form.Item>
                    </Col>

                    {(classificacaoSelecionada === TipoDeRegistro.Subgrupo || classificacaoSelecionada === TipoDeRegistro.Item) && (
                        <Col xs={24} md={7} id={Tutoriais.IDs.campoGrupo}>
                            <Tooltip
                                placement="bottomLeft"
                                title={valoresDoFormularioDePlanoDeContas?.empresa?.codigo == undefined ? "Selecione a empresa para continuar" : undefined}
                            >
                                <Form.Item<TypePlanoContas>
                                    name={["grupo", "codigo"]}
                                    rules={[{ required: true, message: "Por favor, informe um grupo!" }]}
                                    initialValue={props.planoDeContasEmAlteracao?.grupo?.codigo}
                                    label={"Grupo"}
                                >
                                    <SelectComPesquisa
                                        disabled={valoresDoFormularioDePlanoDeContas?.empresa?.codigo == undefined}
                                        placeholder={"Selecione"}
                                        allowClear
                                        notFoundContent={<Empty description={"Oops, parece que nenhum grupo foi adicionado para a empresa selecionada."} />}
                                        options={listarGrupos.data?.data
                                            .filter(
                                                (g) => g.empresa?.codigo != undefined && g.empresa.codigo === valoresDoFormularioDePlanoDeContas.empresa?.codigo
                                            )
                                            .map((x) => {
                                                return {
                                                    value: x.codigo,
                                                    label: x.descricao,
                                                };
                                            })}
                                    />
                                </Form.Item>
                            </Tooltip>
                        </Col>
                    )}
                    {classificacaoSelecionada === TipoDeRegistro.Item && (
                        <Col xs={24} md={7} id={Tutoriais.IDs.campoSubgrupo}>
                            <Tooltip
                                placement="bottomLeft"
                                title={valoresDoFormularioDePlanoDeContas?.grupo?.codigo == undefined ? "Selecione o grupo para continuar" : undefined}
                            >
                                <Form.Item<TypePlanoContas>
                                    name={["subGrupo", "codigo"]}
                                    rules={[{ required: true, message: "Por favor, informe um subgrupo!" }]}
                                    initialValue={props.planoDeContasEmAlteracao?.subGrupo?.codigo}
                                    label={"Subgrupo"}
                                    style={{ margin: 0 }}
                                >
                                    <SelectComPesquisa
                                        placeholder={"Selecione"}
                                        disabled={valoresDoFormularioDePlanoDeContas?.grupo?.codigo == undefined}
                                        notFoundContent={<Empty description={"Oops, parece que nenhum subgrupo foi adicionado para o grupo em questão."} />}
                                        allowClear
                                        options={listarSubGrupos.data?.data
                                            .filter((s) => s.grupo?.codigo != undefined && s.grupo.codigo === valoresDoFormularioDePlanoDeContas?.grupo?.codigo)
                                            .map((x) => {
                                                return {
                                                    value: x.codigo,
                                                    label: x.descricao,
                                                };
                                            })}
                                    />
                                </Form.Item>
                            </Tooltip>
                        </Col>
                    )}
                </Row>
            </Form>

            <Divider />

            {classificacaoSelecionada === TipoDeRegistro.Item && (
                <div id={Tutoriais.IDs.formularioDeCentroDeCusto} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <Form<TypeProporcao> form={formularioDeProporcoes} layout="vertical" onFinish={handleIncluirCentroCusto} autoComplete="off">
                        <Row>
                            <div>
                                <h3>
                                    {" "}
                                    Proporção por Centro de Custo{" "}
                                    <QuestionCircleTwoTone
                                        id={Tutoriais.IDs.botaoAjudaDoCentroDeCusto}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                            setMostrarTutorialDoCadastroDeProporcoes(true);
                                        }}
                                    />{" "}
                                </h3>
                            </div>
                            <Divider style={{ marginTop: 0 }} />
                        </Row>

                        <Row gutter={16} style={{ margin: 0 }}>
                            <Col xs={24} lg={10} id={Tutoriais.IDs.campoCentroDeCustoDoCentroDeCusto} style={{ marginBottom: 15 }}>
                                <Tooltip
                                    placement="bottomLeft"
                                    title={valoresDoFormularioDePlanoDeContas?.empresa?.codigo == undefined ? "Selecione a empresa para continuar" : undefined}
                                >
                                    <Form.Item<TypeProporcao>
                                        name={["centroCusto", "codigo"]}
                                        label="Centro de custo"
                                        rules={[{ required: true, message: "Selecione o plano de contas!" }]}
                                    >
                                        <SelectComPesquisa
                                            placeholder={"Selecione"}
                                            allowClear
                                            disabled={valoresDoFormularioDePlanoDeContas?.empresa?.codigo == undefined}
                                            notFoundContent={
                                                <Empty description={"Oops, parece que nenhum centro de custo foi adicionado para a empresa selecionada."} />
                                            }
                                            options={listarCentroDeCusto.data?.data
                                                .filter(
                                                    (c) =>
                                                        c.empresa?.codigo != undefined &&
                                                        c.empresa.codigo == valoresDoFormularioDePlanoDeContas?.empresa?.codigo
                                                )
                                                .map((x) => {
                                                    return {
                                                        value: x.codigo,
                                                        label: `${x.descricao}`,
                                                    };
                                                })}
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                </Tooltip>
                            </Col>

                            <Col xs={24} lg={10} id={Tutoriais.IDs.campoProporcaoDoCentroDeCusto} style={{ marginBottom: 15 }}>
                                <Form.Item<TypeProporcao> name="proporcao" label="Proporção %" rules={[{ required: true, message: "Insira a proporção!" }]}>
                                    <InputNumber addonAfter={"%"} min={0} max={100 - somaDasProporcoes} decimalSeparator="," style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} lg={4} style={{ display: "flex", alignItems: "end", marginBottom: 15 }}>
                                <Form.Item style={{ flex: 1 }}>
                                    <Tooltip title={somaDasProporcoes >= 100 ? "A soma das proporções já equivale a 100%." : undefined}>
                                        <Button
                                            disabled={somaDasProporcoes >= 100}
                                            type="primary"
                                            key="submit"
                                            htmlType="submit"
                                            id="cadastro-plano-contas-formulario-centro-custo-botao-incluir"
                                            style={{ width: "100%" }}
                                        >
                                            Incluir
                                        </Button>
                                    </Tooltip>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>

                    <Row gutter={16} style={{ margin: 0 }}>
                        <Col xs={24}>
                            <ResponsiveAntdTable<TypeProporcao>
                                dataSource={listaProporcoes}
                                pagination={false}
                                addTitleOn={"sm"}
                                columns={[
                                    {
                                        title: "Centro de custo (descrição)",
                                        dataIndex: ["centroCusto", "descricao"],
                                        key: "codigo",
                                        sorter: (a, b) =>
                                            !a.centroCusto?.descricao || !b.centroCusto?.descricao
                                                ? 0
                                                : a.centroCusto.descricao.localeCompare(b.centroCusto.descricao),
                                        breakpoints: { xs: 12, md: 9 },
                                    },
                                    {
                                        title: "Proporção (%)",
                                        dataIndex: ["proporcao"],
                                        key: "codigo",
                                        sorter: (a, b) => (!a.proporcao || !b.proporcao ? 0 : a.proporcao - b.proporcao),
                                        breakpoints: { xs: 12, md: 9 },
                                    },
                                    {
                                        title: "Opções",
                                        key: "opcoes",
                                        breakpoints: { xs: 24, md: 6 },
                                        render: (_, record) => (
                                            <Flex justify={"center"}>
                                                <Popconfirm
                                                    title="Você tem certeza que deseja remover este registro?"
                                                    okText="Remover"
                                                    onConfirm={() => {
                                                        handleRemoverProporcao(record.codigo);
                                                    }}
                                                >
                                                    <Button
                                                        type="link"
                                                        danger
                                                        size="small"
                                                        icon={<DeleteFilled />}
                                                        className={Tutoriais.Classes.botaoExcluirDoCentroDeCusto}
                                                    >
                                                        Excluir
                                                    </Button>
                                                </Popconfirm>
                                            </Flex>
                                        ),
                                    },
                                ]}
                                footer={() => {
                                    return (
                                        <React.Fragment>
                                            <Typography style={{ textAlign: "end" }}>
                                                Soma: <Typography.Text strong> {somaDasProporcoes}% </Typography.Text>
                                            </Typography>
                                        </React.Fragment>
                                    );
                                }}
                            />
                        </Col>
                    </Row>
                </div>
            )}

            <Row style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                {props.aoCancelar && (
                    <Button
                        danger
                        type={"primary"}
                        style={{ marginRight: 10 }}
                        onClick={() => {
                            props.aoCancelar && props.aoCancelar();
                        }}
                    >
                        Cancelar
                    </Button>
                )}
                <Tooltip
                    title={
                        classificacaoSelecionada == TipoDeRegistro.Item && somaDasProporcoes > 100 ? "A soma das proporções deve equivaler a 100" : undefined
                    }
                >
                    <Button
                        type={"primary"}
                        id={Tutoriais.IDs.botaoSalvar}
                        key="submit"
                        htmlType="submit"
                        disabled={
                            (classificacaoSelecionada == TipoDeRegistro.Item && somaDasProporcoes > 100) ||
                            !usuario?.permissoes
                                .map((p) => p.nome)
                                .includes(props.planoDeContasEmAlteracao != undefined ? Permissoes.AtualizarPlanoDeContas : Permissoes.CriarPlanoDeContas)
                        }
                        onClick={() => {
                            formularioDePlanoDeContas.submit();
                        }}
                        loading={handleInserir.isPending || handleAtualizar.isPending}
                    >
                        Salvar
                    </Button>
                </Tooltip>
            </Row>

            {<AdicionarAjuda tutoriais={tutoriais} />}
        </>
    );
};